// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { combineEngineMetricResults, labelEngineMetricResult } from "./utils.js";
import {
  averageValues,
  extentFromValues,
} from "../core/math.js";
import {
  cylinderKeys,
  cylinderNumberFromKey,
} from "../core/schema.js";
import {
  engineCylinderKeys,
  engineCylinderIdentity,
  projectFlightDataForEngine,
} from "../core/engines.js";
import {
  clampScore,
  elapsedAnchoredSampleForAnalysis,
  median,
  pointCylinderValues,
  rankedQuantile as quantile,
  rollingMedianStep,
  thermalEngineWorkPoint as engineWorkPoint,
} from "./utils.js";
import {
  coolingParameters,
} from "./detectors/cooling.js";
import {
  sensorsParameters,
} from "./detectors/sensors.js";
import {
  coolingRateParameters,
} from "./detectors/cooling-rate.js";

function bestSpeedKey(data) {
  return ["ias", "tas", "groundSpeed"].find((key) => data.some((point) => Number.isFinite(point[key]))) ?? null;
}

function sampleForAnalysis(data, targetCount = coolingParameters.maxAnalysisSamples) {
  return elapsedAnchoredSampleForAnalysis(data, targetCount);
}

function plausibleThermalValue(key, value) {
  if (!Number.isFinite(value)) return null;
  const range = String(key).startsWith("egt")
    ? sensorsParameters.ranges.egt
    : sensorsParameters.ranges.cht;
  return value >= range.min && value <= range.max ? value : null;
}

function plausiblePeerAverage(point, keys, omittedKey) {
  const values = keys
    .filter((key) => key !== omittedKey)
    .map((key) => plausibleThermalValue(key, point[key]))
    .filter(Number.isFinite);
  return values.length ? averageValues(values) : null;
}

function endMedianChange(points, key) {
  const config = coolingParameters.evidenceWindow;
  const values = points.map((point) => point[key]).filter(Number.isFinite);
  if (values.length < Math.max(config.minPowerWindowValues, points.length * config.minPowerWindowShare)) return null;
  const third = Math.max(config.minSegmentSamples, Math.floor(values.length / config.segmentDivisor));
  const start = median(values.slice(0, third));
  const end = median(values.slice(-third));
  return Number.isFinite(start) && Number.isFinite(end) ? end - start : null;
}

function pairedWindowChange(points, valueForPoint) {
  const config = coolingParameters.evidenceWindow;
  const values = points.map(valueForPoint).filter(Number.isFinite);
  if (values.length < Math.max(config.minPowerWindowValues, points.length * config.minPowerWindowShare)) return null;
  const third = Math.max(config.minSegmentSamples, Math.floor(values.length / config.segmentDivisor));
  const start = median(values.slice(0, third));
  const end = median(values.slice(-third));
  return Number.isFinite(start) && Number.isFinite(end) ? end - start : null;
}

function findCoolingEvidenceWindow(data, chtKey, chtKeys, egtKeysForPeer) {
  const config = coolingParameters.evidenceWindow;
  const egtKey = `egt${cylinderNumberFromKey(chtKey)}`;
  const speedKey = bestSpeedKey(data);
  const step = rollingMedianStep(data);
  const sampleCount = Math.max(config.minSamples, Math.round(config.windowSeconds / step));
  const stride = Math.max(config.minStrideSamples, Math.round(sampleCount / config.strideDivisor));
  let best = null;

  for (let start = 0; start + sampleCount <= data.length; start += stride) {
    const window = data.slice(start, start + sampleCount).filter(engineWorkPoint);
    if (window.length < sampleCount * config.minEngineWorkShare) continue;

    const relativeChtValues = window.map((point) => {
      const value = plausibleThermalValue(chtKey, point[chtKey]);
      const peer = plausiblePeerAverage(point, chtKeys, chtKey);
      return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
    }).filter(Number.isFinite);
    if (relativeChtValues.length < window.length * config.minRelativeChtShare) continue;

    const relativeChtChange = pairedWindowChange(window, (point) => {
      const value = plausibleThermalValue(chtKey, point[chtKey]);
      const peer = plausiblePeerAverage(point, chtKeys, chtKey);
      return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
    });
    const relativeEgtChange = pairedWindowChange(window, (point) => {
      const value = plausibleThermalValue(egtKey, point[egtKey]);
      const peer = plausiblePeerAverage(point, egtKeysForPeer, egtKey);
      return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
    });
    const fuelChange = endMedianChange(window, "fuelFlow");
    const rpmChange = endMedianChange(window, "rpm");
    const mapChange = endMedianChange(window, "map");
    const speedChange = speedKey ? endMedianChange(window, speedKey) : null;
    const averageChtDelta = averageValues(relativeChtValues);
    const endCht = median(window.slice(-Math.max(config.minSegmentSamples, Math.floor(window.length / config.segmentDivisor)))
      .map((point) => plausibleThermalValue(chtKey, point[chtKey])).filter(Number.isFinite));
    const powerStable = (Number.isFinite(fuelChange) && Math.abs(fuelChange) <= config.fuelStableChangeGph)
      || (Number.isFinite(rpmChange) && Math.abs(rpmChange) <= config.rpmStableChange)
      || (Number.isFinite(mapChange) && Math.abs(mapChange) <= config.mapStableChangeInHg);
    const combustionMismatch = !Number.isFinite(relativeEgtChange) || Math.abs(relativeEgtChange) <= config.egtMismatchChangeF;
    const speedDrop = Number.isFinite(speedChange) && speedChange <= -config.speedDropKt;
    const qualifies = (averageChtDelta >= config.averageChtDeltaF || endCht >= config.endChtF)
      && (relativeChtChange ?? 0) >= config.relativeChtChangeF
      && combustionMismatch;
    if (!qualifies) continue;

    const suspicion = averageChtDelta
      + Math.max(0, (relativeChtChange ?? 0) - config.relativeChtChangeF) * config.relativeChtChangeWeight
      + Math.max(0, (endCht ?? 0) - config.endChtBaseF) * config.endChtWeight
      + (powerStable ? config.stablePowerBonus : 0)
      + (speedDrop ? config.speedDropBonus : 0);
    const candidate = {
      startTime: window[0].elapsedSeconds ?? start,
      endTime: window.at(-1).elapsedSeconds ?? start + sampleCount - 1,
      averageChtDelta,
      relativeChtChange,
      relativeEgtChange,
      fuelChange,
      rpmChange,
      mapChange,
      speedKey,
      speedChange,
      endCht,
      powerStable,
      combustionMismatch,
      speedDrop,
      suspicion,
    };
    if (!best || candidate.suspicion > best.suspicion) best = candidate;
  }

  return best;
}

// A persistent cylinder-to-peer temperature difference can trigger the
// cooling review without the progressive warming pattern required by the
// stronger evidence window above. Preserve one stable, representative window
// for that case so downstream summaries can name the actual flight phase.
function findRepresentativeCoolingWindow(data, chtKey, chtKeys) {
  const config = coolingParameters.evidenceWindow;
  const step = rollingMedianStep(data);
  const sampleCount = Math.max(config.minSamples, Math.round(config.windowSeconds / step));
  const stride = Math.max(config.minStrideSamples, Math.round(sampleCount / config.strideDivisor));
  let best = null;

  for (let start = 0; start + sampleCount <= data.length; start += stride) {
    const window = data.slice(start, start + sampleCount).filter(engineWorkPoint);
    if (window.length < sampleCount * config.minEngineWorkShare) continue;
    const deltas = window.map((point) => {
      const value = plausibleThermalValue(chtKey, point[chtKey]);
      const peer = plausiblePeerAverage(point, chtKeys, chtKey);
      return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
    }).filter(Number.isFinite);
    if (deltas.length < window.length * config.minRelativeChtShare) continue;

    const representativeDelta = median(deltas);
    if (!Number.isFinite(representativeDelta)) continue;
    const fuelChange = endMedianChange(window, "fuelFlow");
    const rpmChange = endMedianChange(window, "rpm");
    const mapChange = endMedianChange(window, "map");
    const powerStable = (Number.isFinite(fuelChange) && Math.abs(fuelChange) <= config.fuelStableChangeGph)
      || (Number.isFinite(rpmChange) && Math.abs(rpmChange) <= config.rpmStableChange)
      || (Number.isFinite(mapChange) && Math.abs(mapChange) <= config.mapStableChangeInHg);
    const candidate = {
      startTime: window[0].elapsedSeconds ?? start,
      endTime: window.at(-1).elapsedSeconds ?? start + sampleCount - 1,
      averageChtDelta: averageValues(deltas),
      representativeChtDelta: representativeDelta,
      powerStable,
      representative: true,
    };
    const candidateRank = representativeDelta + (powerStable ? config.stablePowerBonus : 0);
    const bestRank = best
      ? best.representativeChtDelta + (best.powerStable ? config.stablePowerBonus : 0)
      : -Infinity;
    if (candidateRank > bestRank) best = candidate;
  }
  return best;
}

function analyzeCoolingEfficiencyForKeys(data, chtSourceKeys, egtSourceKeys, options = {}) {
  const analysisData = sampleForAnalysis(data);
  const chtKeys = chtSourceKeys.filter((key) => analysisData.some((point) => Number.isFinite(point[key])));
  const egtKeys = egtSourceKeys.filter((key) => analysisData.some((point) => Number.isFinite(point[key])));
  if (chtKeys.length < coolingParameters.minChtChannels) {
    return { key: "cooling", score: null, note: "Need multiple CHT channels", details: {} };
  }

  const workData = analysisData.filter(engineWorkPoint);
  if (workData.length < coolingParameters.minEngineRunSamples) {
    return { key: "cooling", score: null, note: "Need engine-run CHT samples", details: {} };
  }

  const summaries = chtKeys.map((key) => {
    const summaryConfig = coolingParameters.summary;
    const egtKey = `egt${cylinderNumberFromKey(key)}`;
    const rows = workData.map((point) => {
      const cht = plausibleThermalValue(key, point[key]);
      const peerCht = plausiblePeerAverage(point, chtKeys, key);
      const egt = plausibleThermalValue(egtKey, point[egtKey]);
      const peerEgt = plausiblePeerAverage(point, egtKeys, egtKey);
      if (!Number.isFinite(cht) || !Number.isFinite(peerCht)) return null;
      return {
        cht,
        chtDelta: cht - peerCht,
        egtDelta: Number.isFinite(egt) && Number.isFinite(peerEgt) ? egt - peerEgt : null,
      };
    }).filter(Boolean);
    const chtDeltas = rows.map((row) => row.chtDelta).filter(Number.isFinite);
    const egtDeltas = rows.map((row) => row.egtDelta).filter(Number.isFinite);
    const p95Delta = quantile(chtDeltas, 0.95) ?? 0;
    const maxDelta = extentFromValues(chtDeltas)?.max ?? 0;
    const maxCht = extentFromValues(rows.map((row) => row.cht))?.max ?? 0;
    const p95Cht = quantile(rows.map((row) => row.cht), 0.95) ?? maxCht;
    const p75EgtDelta = quantile(egtDeltas.map(Math.abs), 0.75) ?? null;
    const evidenceWindow = findCoolingEvidenceWindow(analysisData, key, chtKeys, egtKeys);
    const representativeWindow = findRepresentativeCoolingWindow(analysisData, key, chtKeys);
    const mismatchBonus = p95Delta >= summaryConfig.mismatchP95DeltaF
      && (!Number.isFinite(p75EgtDelta) || p75EgtDelta <= summaryConfig.mismatchP75EgtDeltaF)
      ? summaryConfig.mismatchBonus
      : 0;
    const suspicion = Math.max(0, p95Delta - summaryConfig.p95DeltaBaseF) * summaryConfig.p95DeltaWeight
      + Math.max(0, maxDelta - summaryConfig.maxDeltaBaseF) * summaryConfig.maxDeltaWeight
      + Math.max(0, p95Cht - summaryConfig.p95ChtBaseF) * summaryConfig.p95ChtWeight
      + Math.max(0, maxCht - summaryConfig.maxChtBaseF) * summaryConfig.maxChtWeight
      + (evidenceWindow?.suspicion ?? 0) * summaryConfig.evidenceWindowWeight
      + mismatchBonus;
    return {
      key,
      cylinder: cylinderNumberFromKey(key),
      ...(options.engineDescriptors ? engineCylinderIdentity(cylinderNumberFromKey(key), options.engineDescriptors) : {}),
      p95Delta,
      maxDelta,
      p95Cht,
      maxCht,
      p75EgtDelta,
      evidenceWindow,
      representativeWindow,
      suspicion,
    };
  }).filter((summary) => Number.isFinite(summary.p95Delta));

  if (!summaries.length) {
    return { key: "cooling", score: null, note: "No comparable CHT channels", details: {} };
  }

  const top = summaries.sort((a, b) => b.suspicion - a.suspicion)[0];
  const score = top.suspicion > 0 ? clampScore(100 - Math.min(coolingParameters.scoreSuspicionCap, top.suspicion)) : coolingParameters.cleanScore;
  if (score < coolingParameters.reviewScoreThreshold && !top.evidenceWindow) {
    top.evidenceWindow = top.representativeWindow;
  }
  let note = "CHT pattern matches the other cylinders";
  if (score < coolingParameters.reviewScoreThreshold) {
    const delta = Math.round(top.p95Delta);
    note = `${top.cylinderShortLabel ?? `Cyl ${top.cylinder}`} runs ${delta} F hotter than the other cylinders`;
  } else if (bestSpeedKey(analysisData)) {
    note = "Cooling check includes airspeed context";
  }
  return {
    key: "cooling",
    score,
    note,
    details: {
      top,
      summaries,
      speedKey: bestSpeedKey(analysisData),
      workSamples: workData.length,
    },
  };
}

export function analyzeCoolingEfficiency(data, options = {}) {
  const descriptors = Array.isArray(options.engineDescriptors) ? options.engineDescriptors : null;
  if (descriptors && descriptors.length > 1) {
    const perEngine = descriptors.map((engine) => {
      const chtSourceKeys = engineCylinderKeys(engine, "cht");
      const egtSourceKeys = engineCylinderKeys(engine, "egt");
      const projected = projectFlightDataForEngine(data, engine);
      const result = labelEngineMetricResult(analyzeCoolingEfficiencyForKeys(projected, chtSourceKeys, egtSourceKeys, { engineDescriptors: descriptors }), descriptors);
      return {
        engineKey: engine.engineKey ?? engine.id ?? `engine${(engine.index ?? 0) + 1}`,
        label: engine.label,
        shortLabel: engine.shortLabel ?? engine.label,
        result,
      };
    }).filter(Boolean);
    const scoredEngines = perEngine.filter((entry) => Number.isFinite(entry.result.score));
    if (scoredEngines.length < 2) return combineEngineMetricResults(perEngine);
    if (scoredEngines.length >= 2) {
      const worst = scoredEngines.reduce((acc, entry) => acc === null || entry.result.score < acc.result.score ? entry : acc, null);
      const score = scoredEngines.reduce((acc, entry) => Math.min(acc, entry.result.score), 100);
      const topByEngine = perEngine
        .map((entry) => entry.result.details?.top
          ? { ...entry.result.details.top, engineKey: entry.engineKey, engineLabel: entry.label, engineShortLabel: entry.shortLabel }
          : null)
        .filter(Boolean)
        .sort((a, b) => (b.suspicion ?? 0) - (a.suspicion ?? 0));
      const top = topByEngine[0] ?? null;
      const note = top && score < coolingParameters.reviewScoreThreshold
        ? `${top.cylinderShortLabel} runs ${Math.round(top.p95Delta)} F hotter than the other cylinders`
        : "CHT pattern matches the other cylinders";
      return {
        key: "cooling",
        score,
        note,
        details: {
          ...worst.result.details,
          top,
          summaries: perEngine.flatMap((entry) => (entry.result.details?.summaries ?? []).map((summary) => ({
            ...summary,
            engineKey: entry.engineKey,
            engineShortLabel: entry.shortLabel,
          }))),
          engines: perEngine.map((entry) => ({
            engineKey: entry.engineKey,
            label: entry.label,
            shortLabel: entry.shortLabel,
            score: entry.result.score,
            note: entry.result.note,
            details: entry.result.details,
          })),
          worstEngineKey: worst.engineKey,
        },
      };
    }
  }
  return analyzeCoolingEfficiencyForKeys(data, cylinderKeys("cht"), cylinderKeys("egt"));
}

function coolingRateChtKeys(data) {
  const perCylinder = cylinderKeys("cht").filter((key) => data.some((point) => Number.isFinite(point[key])));
  if (perCylinder.length) return perCylinder;
  return data.some((point) => Number.isFinite(point.cht)) ? ["cht"] : [];
}

function coolingRateChannelLabel(key) {
  const cylinder = cylinderNumberFromKey(key);
  return Number.isFinite(cylinder) ? `Cyl ${cylinder}` : "CHT";
}

function coolingRateWorkPoint(point, key) {
  const phase = String(point?.phase ?? "");
  if (phase === "Startup" || phase === "Shutdown") return false;
  if (engineWorkPoint(point)) return true;
  return Number.isFinite(point?.[key]) && point[key] > coolingRateParameters.minWorkChtF;
}

function coolingRateSamplesForKey(data, key) {
  const raw = data.map((point, index) => ({
    point,
    index,
    value: point[key],
    elapsedSeconds: point.elapsedSeconds ?? index,
  })).filter((sample) => (
    Number.isFinite(sample.value)
    && Number.isFinite(sample.elapsedSeconds)
    && coolingRateWorkPoint(sample.point, key)
  ));
  return raw.map((sample, index) => {
    const prev = raw[index - 1]?.value;
    const next = raw[index + 1]?.value;
    if (!Number.isFinite(prev) || !Number.isFinite(next)) return sample;
    const triplet = [prev, sample.value, next].slice().sort((a, b) => a - b);
    return { ...sample, value: triplet[1] };
  });
}

function coolingRateReviewWindow(startTime, endTime, samples) {
  const firstTime = samples[0]?.elapsedSeconds;
  const lastTime = samples.at(-1)?.elapsedSeconds;
  const reviewStartTime = Math.max(
    Number.isFinite(firstTime) ? firstTime : startTime,
    startTime - coolingRateParameters.reviewContextBeforeSeconds,
  );
  const reviewEndTime = Math.min(
    Number.isFinite(lastTime) ? lastTime : endTime,
    endTime + coolingRateParameters.reviewContextAfterSeconds,
  );
  return { reviewStartTime, reviewEndTime };
}

function coolingRatePercentPowerContext(samples) {
  const config = coolingRateParameters.percentPower;
  const values = samples.map((sample) => sample.point?.percentPower).filter(Number.isFinite);
  if (values.length < config.minSamples) {
    return {
      percentPowerBefore: null,
      percentPowerAfter: null,
      percentPowerDelta: null,
      percentPowerRange: null,
      percentPowerAvg: null,
      contextTags: [],
      contextScoreRelief: 0,
    };
  }

  const segmentSize = Math.max(1, Math.floor(values.length / 3));
  const before = median(values.slice(0, segmentSize));
  const after = median(values.slice(-segmentSize));
  const extent = extentFromValues(values);
  const average = averageValues(values);
  const delta = Number.isFinite(before) && Number.isFinite(after) ? after - before : null;
  const range = Number.isFinite(extent?.min) && Number.isFinite(extent?.max) ? extent.max - extent.min : null;
  const tags = [];
  if (Number.isFinite(delta) && delta <= -config.minReduction) tags.push("percent-power-reduction-context");
  if (Number.isFinite(after) && after <= config.lowPower) tags.push("low-percent-power-context");
  if (Number.isFinite(delta) && delta >= config.minReduction) tags.push("percent-power-increase-context");
  if (
    Number.isFinite(range)
    && range <= config.stableRange
    && Number.isFinite(average)
    && average >= config.highPower
  ) {
    tags.push("stable-high-percent-power");
  }

  const reductionRelief = tags.includes("percent-power-reduction-context") ? config.reductionScoreRelief : 0;
  const lowPowerRelief = tags.includes("low-percent-power-context") ? config.lowPowerScoreRelief : 0;
  return {
    percentPowerBefore: before,
    percentPowerAfter: after,
    percentPowerDelta: delta,
    percentPowerRange: range,
    percentPowerAvg: average,
    contextTags: tags,
    contextScoreRelief: reductionRelief + lowPowerRelief,
  };
}

// Materialise a full cooling-rate candidate for one [startIndex..endIndex]
// window. This is the O(window) part: coolingRatePercentPowerContext slices and
// re-medians the whole span. Kept in its own function so the window scan can
// call it ONLY for the pairs it actually returns (see below).
function coolingRateCandidateAt(samples, key, startIndex, endIndex) {
  const start = samples[startIndex];
  const end = samples[endIndex];
  const dt = end.elapsedSeconds - start.elapsedSeconds;
  const drop = start.value - end.value;
  const rateFpm = drop / dt * 60;
  const percentPowerContext = coolingRatePercentPowerContext(samples.slice(startIndex, endIndex + 1));
  return {
    key,
    cylinder: cylinderNumberFromKey(key),
    label: coolingRateChannelLabel(key),
    rateFpm,
    drop,
    durationSeconds: dt,
    startTime: start.elapsedSeconds,
    endTime: end.elapsedSeconds,
    ...coolingRateReviewWindow(start.elapsedSeconds, end.elapsedSeconds, samples),
    startCht: start.value,
    endCht: end.value,
    startPhase: start.point.phase ?? "",
    endPhase: end.point.phase ?? "",
    percentPowerBefore: percentPowerContext.percentPowerBefore,
    percentPowerAfter: percentPowerContext.percentPowerAfter,
    percentPowerDelta: percentPowerContext.percentPowerDelta,
    percentPowerRange: percentPowerContext.percentPowerRange,
    percentPowerAvg: percentPowerContext.percentPowerAvg,
    contextTags: percentPowerContext.contextTags,
    contextScoreRelief: percentPowerContext.contextScoreRelief,
  };
}

// The window scan visits O(n·W) (start,end) pairs (W = samples within
// maxWindowSeconds). The previous implementation built a FULL candidate —
// including the O(window) percentPower context slice — for every one of them,
// making the scan O(n·W²). On a densely-sampled short flight (a 2 h log at full
// rate packs far more samples into the 75 s window than a 5 h log downsampled to
// the cap) that quadratic blew past the ingest CPU budget — the 2026-07 prod
// "analyze timed out" failures. We now compute only the cheap rateFpm for every
// pair, and materialise the expensive context solely for the pairs we return:
// the threshold-crossing `events`, and the max-rate tie group `best` is chosen
// from. Output is identical — `best` is still the highest rateFpm, tie-broken by
// contextScoreRelief, and `events` are unchanged and in the same order.
function coolingRateCandidatesForKey(data, key, { thresholdFpm = coolingRateParameters.triggerFpm } = {}) {
  const samples = coolingRateSamplesForKey(data, key);
  const eventPairs = [];
  // Max-rate tie group, collected cheaply (rateFpm only). Reset on a strictly
  // greater rate so it always holds exactly the pairs tied at the running max,
  // in first-seen order — mirroring the original sequential best-update.
  let maxRate = -Infinity;
  let maxPairs = [];
  for (let endIndex = 1; endIndex < samples.length; endIndex += 1) {
    const end = samples[endIndex];
    for (let startIndex = endIndex - 1; startIndex >= 0; startIndex -= 1) {
      const start = samples[startIndex];
      const dt = end.elapsedSeconds - start.elapsedSeconds;
      if (!Number.isFinite(dt) || dt <= 0) continue;
      if (dt > coolingRateParameters.maxWindowSeconds) break;
      if (dt < coolingRateParameters.minWindowSeconds) continue;
      const drop = start.value - end.value;
      if (!(drop > 0)) continue;
      const rateFpm = drop / dt * 60;
      if (rateFpm > maxRate + 1e-9) {
        maxRate = rateFpm;
        maxPairs = [{ startIndex, endIndex }];
      } else if (Math.abs(rateFpm - maxRate) <= 1e-9) {
        maxPairs.push({ startIndex, endIndex });
      }
      if (rateFpm >= thresholdFpm) eventPairs.push({ startIndex, endIndex });
    }
  }
  const events = eventPairs.map(({ startIndex, endIndex }) => coolingRateCandidateAt(samples, key, startIndex, endIndex));
  let best = null;
  for (const { startIndex, endIndex } of maxPairs) {
    const candidate = coolingRateCandidateAt(samples, key, startIndex, endIndex);
    if (
      !best
      || candidate.rateFpm > best.rateFpm + 1e-9
      || (Math.abs(candidate.rateFpm - best.rateFpm) <= 1e-9 && (candidate.contextScoreRelief ?? 0) > (best.contextScoreRelief ?? 0))
    ) {
      best = candidate;
    }
  }
  return { samples, best, events };
}

export function clusteredCoolingRateEvents(events) {
  const clusters = [];
  events
    .slice()
    .sort((a, b) => String(a.key).localeCompare(String(b.key)) || a.startTime - b.startTime || b.rateFpm - a.rateFpm)
    .forEach((event) => {
      const cluster = clusters.findLast((candidate) => (
        candidate.key === event.key
        && event.startTime <= candidate.endTime + coolingRateParameters.eventClusterGapSeconds
      ));
      if (cluster) {
        const startTime = Math.min(cluster.startTime, event.startTime);
        const endTime = Math.max(cluster.endTime, event.endTime);
        const contextTags = [...new Set([...(cluster.contextTags ?? []), ...(event.contextTags ?? [])])];
        const contextScoreRelief = Math.max(cluster.contextScoreRelief ?? 0, event.contextScoreRelief ?? 0);
        const hasReviewWindow = Number.isFinite(cluster.reviewStartTime)
          || Number.isFinite(cluster.reviewEndTime)
          || Number.isFinite(event.reviewStartTime)
          || Number.isFinite(event.reviewEndTime);
        const reviewStartTime = hasReviewWindow ? Math.min(
          Number.isFinite(cluster.reviewStartTime) ? cluster.reviewStartTime : cluster.startTime,
          Number.isFinite(event.reviewStartTime) ? event.reviewStartTime : event.startTime,
        ) : null;
        const reviewEndTime = hasReviewWindow ? Math.max(
          Number.isFinite(cluster.reviewEndTime) ? cluster.reviewEndTime : cluster.endTime,
          Number.isFinite(event.reviewEndTime) ? event.reviewEndTime : event.endTime,
        ) : null;
        const count = (cluster.count ?? 1) + 1;
        if (event.rateFpm > cluster.rateFpm) {
          Object.assign(cluster, event, { startTime, endTime, count });
        } else {
          cluster.startTime = startTime;
          cluster.endTime = endTime;
          cluster.count = count;
        }
        if (contextTags.length) cluster.contextTags = contextTags;
        else delete cluster.contextTags;
        if (contextScoreRelief > 0) cluster.contextScoreRelief = contextScoreRelief;
        else delete cluster.contextScoreRelief;
        if (hasReviewWindow) {
          cluster.reviewStartTime = reviewStartTime;
          cluster.reviewEndTime = reviewEndTime;
        }
        return;
      }
      clusters.push({ ...event, count: 1 });
    });
  return clusters.sort((a, b) => b.rateFpm - a.rateFpm);
}

// Collapse each per-cylinder summary's raw cooling-rate event list to its
// clustered form for persistence. The window scan emits one event per
// qualifying [start,end] window, which on a densely-sampled flight is O(n·W)
// near-duplicate sub-windows of the same cooling episode — one prod TXi log
// produced 473k events / a 224 MB analysis artifact whose gzip+upload tripped
// the storage write timeout. Scoring already collapses these to clusters
// (`eventCount = clusteredCoolingRateEvents(...).length`), so the raw list is
// redundant once every consumer of it has run. Call this AFTER
// augmentSensorValidityWithCoolingRate (the only reader of the raw sub-window
// list) so scores, notes, `top`, the top-level clustered `events`, and sensor
// cues are all still computed from the full raw list and stay unchanged; only
// the persisted `summaries[].events` array shrinks.
export function compactCoolingRateSummaryEvents(metric) {
  const summaries = metric?.details?.summaries;
  if (!Array.isArray(summaries)) return metric;
  for (const summary of summaries) {
    if (Array.isArray(summary?.events) && summary.events.length) {
      summary.events = clusteredCoolingRateEvents(summary.events);
    }
  }
  return metric;
}

export function coolingRateScoreForRate(rateFpm, { eventCount = 0, triggeredCylinderCount = 1, thresholdFpm = coolingRateParameters.triggerFpm } = {}) {
  if (!Number.isFinite(rateFpm)) return null;
  if (rateFpm < thresholdFpm) return coolingRateParameters.score.cleanScore;
  const excess = Math.max(0, rateFpm - thresholdFpm);
  const rawScore = coolingRateParameters.score.baseTriggeredScore
    - excess * coolingRateParameters.score.excessRatePenalty
    - Math.max(0, eventCount - 1) * coolingRateParameters.score.repeatEventPenalty
    - Math.max(0, triggeredCylinderCount - 1) * coolingRateParameters.score.additionalCylinderPenalty;
  return clampScore(rawScore);
}

function contextualCoolingRateScoreForRate(rateFpm, {
  eventCount = 0,
  triggeredCylinderCount = 1,
  thresholdFpm = coolingRateParameters.triggerFpm,
  contextScoreRelief = 0,
} = {}) {
  const baseScore = coolingRateScoreForRate(rateFpm, { eventCount, triggeredCylinderCount, thresholdFpm });
  if (!Number.isFinite(baseScore) || contextScoreRelief <= 0 || rateFpm < thresholdFpm) return baseScore;
  return Math.min(
    coolingRateParameters.percentPower.maxContextualScore,
    clampScore(baseScore + contextScoreRelief),
  );
}

export function coolingRateNoteForRate(rateFpm, label = "CHT", thresholdFpm = coolingRateParameters.triggerFpm) {
  if (!Number.isFinite(rateFpm)) return "Need CHT samples";
  return rateFpm >= thresholdFpm
    ? `${label} cooled at ${Math.round(rateFpm)} F/min`
    : `${label} max ${Math.round(rateFpm)} F/min`;
}

export function analyzeChtCoolingRate(data, options = {}) {
  const thresholdFpm = Number.isFinite(Number(options.thresholdFpm))
    ? Number(options.thresholdFpm)
    : coolingRateParameters.triggerFpm;
  const analysisData = sampleForAnalysis(data, coolingRateParameters.maxAnalysisSamples);
  const keys = coolingRateChtKeys(analysisData);
  if (!keys.length) {
    return { key: "coolingRate", score: null, note: "No CHT channel data", details: { thresholdFpm } };
  }

  const summaries = keys.map((key) => {
    const result = coolingRateCandidatesForKey(analysisData, key, { thresholdFpm });
    const maxRateFpm = result.best?.rateFpm ?? null;
    const eventCount = clusteredCoolingRateEvents(result.events).length;
    const label = coolingRateChannelLabel(key);
    return {
      key,
      cylinder: cylinderNumberFromKey(key),
      label,
      sampleCount: result.samples.length,
      maxRateFpm,
      score: contextualCoolingRateScoreForRate(maxRateFpm, {
        eventCount,
        thresholdFpm,
        contextScoreRelief: result.best?.contextScoreRelief ?? 0,
      }),
      note: coolingRateNoteForRate(maxRateFpm, label, thresholdFpm),
      top: result.best,
      events: result.events,
    };
  });
  const usableSummaries = summaries.filter((summary) => Number.isFinite(summary.maxRateFpm));
  if (!usableSummaries.length) {
    return { key: "coolingRate", score: null, note: "Need CHT samples with elapsed time", details: { thresholdFpm, summaries } };
  }

  const top = usableSummaries
    .map((summary) => summary.top)
    .filter(Boolean)
    .sort((a, b) => b.rateFpm - a.rateFpm)[0];
  const events = clusteredCoolingRateEvents(summaries.flatMap((summary) => summary.events));
  const triggeredCylinderCount = summaries.filter((summary) => (summary.maxRateFpm ?? 0) >= thresholdFpm).length;
  const maxRate = top?.rateFpm ?? 0;
  const score = contextualCoolingRateScoreForRate(maxRate, {
    eventCount: events.length,
    triggeredCylinderCount,
    thresholdFpm,
    contextScoreRelief: top?.contextScoreRelief ?? 0,
  });
  const hasPowerReductionContext = top?.contextTags?.includes("percent-power-reduction-context");
  const note = maxRate >= thresholdFpm
    ? `${top.label} cooled at ${Math.round(maxRate)} F/min${hasPowerReductionContext ? " during power reduction" : ""}`
    : `Max CHT cooling ${Math.round(maxRate)} F/min`;
  return {
    key: "coolingRate",
    score,
    note,
    details: {
      thresholdFpm,
      minWindowSeconds: coolingRateParameters.minWindowSeconds,
      maxWindowSeconds: coolingRateParameters.maxWindowSeconds,
      top,
      events,
      summaries,
      triggeredCylinderCount,
    },
  };
}

// Per-sample cooling-rate trace for one CHT channel, for the Overview cooling-rate
// band graph. At each engine-run sample we record the steepest cooling rate over a
// detector window (12-75 s) ending there, so the trace's peak matches the scored
// max rate. Reuses the exact sampling + window math the score uses, so the picture
// and the number agree.
export function coolingRateTraceForKey(data, key) {
  const analysisData = sampleForAnalysis(data, coolingRateParameters.maxAnalysisSamples);
  const samples = coolingRateSamplesForKey(analysisData, key);
  const trace = samples.map((end, endIndex) => {
    let rate = 0;
    for (let startIndex = endIndex - 1; startIndex >= 0; startIndex -= 1) {
      const dt = end.elapsedSeconds - samples[startIndex].elapsedSeconds;
      if (dt > coolingRateParameters.maxWindowSeconds) break;
      if (dt < coolingRateParameters.minWindowSeconds) continue;
      const drop = samples[startIndex].value - end.value;
      if (drop > 0) rate = Math.max(rate, (drop / dt) * 60);
    }
    return { t: end.elapsedSeconds, rate };
  });
  return {
    key,
    cylinder: cylinderNumberFromKey(key),
    label: coolingRateChannelLabel(key),
    samples: trace,
    sampleCount: trace.length,
  };
}

// Per-cylinder CHT-over-time trace (+ OAT / airspeed context) for the Overview
// cooling-margin band graph. Uses the SAME sampling + engine-work gate the cooling
// (efficiency) score uses, so the CHT line is consistent with the scored p95Cht.
// OAT (point.oat, already F) and airspeed (bestSpeedKey: ias > tas > groundSpeed)
// ride along when logged; both are null when the monitor doesn't record them.
export function coolingMarginTraceForKey(data, key) {
  const analysisData = sampleForAnalysis(data);
  const workData = analysisData.filter(engineWorkPoint);
  const speedKey = bestSpeedKey(analysisData);
  let rows = workData
    .map((point) => ({
      t: point.elapsedSeconds,
      cht: point[key],
      oat: point.oat,
      speed: speedKey ? point[speedKey] : null,
    }))
    .filter((row) => Number.isFinite(row.cht) && Number.isFinite(row.t));
  // 3-point median smooth on CHT to drop single-sample spikes (same idiom the
  // cooling-rate sampler uses).
  rows = rows.map((row, index) => {
    const prev = rows[index - 1]?.cht;
    const next = rows[index + 1]?.cht;
    if (!Number.isFinite(prev) || !Number.isFinite(next)) return row;
    const triplet = [prev, row.cht, next].slice().sort((a, b) => a - b);
    return { ...row, cht: triplet[1] };
  });
  // Stride-decimate to ~600 points after smoothing (kept cheap — this runs sync on
  // the main thread when a cylinder is opened).
  const stride = Math.max(1, Math.ceil(rows.length / 600));
  const kept = rows.filter((_, index) => index % stride === 0);
  if (rows.length && kept[kept.length - 1] !== rows[rows.length - 1]) kept.push(rows[rows.length - 1]);
  const oats = rows.map((row) => row.oat).filter(Number.isFinite);
  const oatExtent = oats.length ? extentFromValues(oats) : null;
  return {
    key,
    cylinder: cylinderNumberFromKey(key),
    label: coolingRateChannelLabel(key),
    // Each kept sample carries its OAT / airspeed (null when not logged) so the
    // renderer can align the context strip + cooling-demand shading to the CHT line.
    samples: kept.map((row) => ({
      t: row.t,
      cht: row.cht,
      oat: Number.isFinite(row.oat) ? row.oat : null,
      speed: Number.isFinite(row.speed) ? row.speed : null,
    })),
    speedKey,
    hasOat: oats.length > 0,
    hasSpeed: Boolean(speedKey),
    oat: oatExtent ? { min: oatExtent.min, avg: averageValues(oats), max: oatExtent.max } : null,
    sampleCount: kept.length,
  };
}
